import { IOmniaSession, LocaleString, MapDependencies, react } from "../../omnia"
import { IDatalayersService } from "../../types"
import { dispatchRefresh } from "../actions"
import { createApi } from "../api"
import { TerramapContext } from "../context"
import { getLayerLoader } from "../map/layerLoader"
import { useMapContext } from "../map/mapContext"
import { getInitialState, updateState } from "../state"
import { Form } from "./Form"
import { MapView } from "./MapView"

interface Props extends react.ReactAngularComponentContext {

    arguments: {
        mapDependencies: MapDependencies,
        svc: IDatalayersService
    }

}

export function TerramapManager({ setHeader, close, arguments: { mapDependencies, svc }, refresh }: Props) {

    const session = svc.getSession()

    const { state, dispatch, api, layerLoader } = useSetup(session, svc, refresh)

    useStandardHeader(setHeader)

    const mapContext = useMapContext(svc, mapDependencies, layerLoader)

    return <TerramapContext.Provider value={{ api, close, dispatch, mapContext }}>

        <div className="std-layout omnia-datalayers-terramap">

            <Form state={state} />

            <MapView state={state} mapContext={mapContext} />

        </div>

    </TerramapContext.Provider>

}


/**
 * Global setup hook:
 * - Creates the reducer and state
 * - Creates an instance of the API
 * - Initialises/refreshes the state if required
 */
function useSetup(session: IOmniaSession, svc: IDatalayersService, refresh: react.ReactAngularComponentContext["refresh"]) {

    const [state, dispatch] = React.useReducer(updateState, getInitialState())

    const [shouldRefresh, setShouldRefresh] = React.useState(true)

    const api = React.useMemo(() => createApi(session, svc), [session])

    const layerLoader = React.useMemo(() => getLayerLoader(svc), [svc])

    React.useEffect(() => setShouldRefresh(true), [refresh])

    React.useEffect(() => {

        if (shouldRefresh) {
            setShouldRefresh(false)
            layerLoader.clear()
            dispatchRefresh(dispatch, api, state.selection, refresh)
        }

    }, [api, shouldRefresh])

    return {
        state,
        dispatch,
        api,
        layerLoader,
    }

}

/**
 * Sets the standard header for this feature
 */
function useStandardHeader(setHeader: react.ReactAngularComponentContext["setHeader"]) {

    React.useEffect(() => {

        const header = {
            title: "Manage Terramap layers" as LocaleString,
            icon: "fas fa-sliders-h"
        }

        setHeader(header)

    }, [setHeader])

}
